import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { JobsCreatedByUser } from '../../../types/reporting';
import { useTheme } from '@mui/material/styles';
import { reportingService } from '../../../services/reportingService';

interface SalesPerformanceChartProps {
  data?: JobsCreatedByUser[];
  loading?: boolean;
}

const SalesPerformanceChart: React.FC<SalesPerformanceChartProps> = ({
  data,
  loading = false,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const chartData = data?.map((user) => ({
    name: user.userName.split(' ')[0], // First name only for chart
    fullName: user.userName,
    revenue: user.totalValue,
    jobs: user.jobCount,
    converted: user.jobsConverted,
  })) || [];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Sales by User
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 60, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickFormatter={(value: number) => reportingService.formatCurrency(value)}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  width={60}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 4,
                  }}
                  formatter={(value: number, name: string) => [
                    name === 'Revenue' ? reportingService.formatCurrency(value) : value,
                    name,
                  ]}
                  labelFormatter={(label: string, payload: any[]) =>
                    payload?.[0]?.payload?.fullName || label
                  }
                />
                <Legend />
                <Bar
                  dataKey="revenue"
                  name="Revenue"
                  fill={theme.palette.primary.main}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography color="text.secondary">No data available</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SalesPerformanceChart;
